/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { getIsEditing } from 'selectors/navigator'
import { classNames } from 'utils/utils'
import {
  COMPLEX,
  SIMPLE,
  CARRY_LOGIC_SET,
  CARRY_TIMER,
  SELF_REFERENCE,
  TIMER,
  ANALOG_INPUT,
  DIGITAL_INPUT,
  CAN_INPUT,
  NATIVE_INPUT,
  SWITCH_INPUT,
} from 'utils/constants'
import { inc, propOr } from 'ramda'
import { makeSignalSelector } from 'selectors/feature/inputs'
import {
  InputSelectionEditor,
  EdgeEditor,
  ReadOnly,
} from 'components/feature/NavigatorPanes/editors/base'
import Editor from 'components/feature/Editor'
import InputtingDataFrom from 'components/feature/NavigatorPanes/InputtingDataFrom'

import { makeTempId } from 'utils/identity'
import strings from 'utils/strings'
import {
  AnalogInputEditor,
  DigitalInputEditor,
  SimpleEditor,
  TimerEditor,
  CanInputEditor,
  SubQueryCarryEditor,
} from 'components/feature/NavigatorPanes/editors'
import OutputtingDataTo from 'components/feature/NavigatorPanes/OutputtingDataTo'
import { Header } from 'components/patterns/Typography'
import DrawerDivider from 'components/patterns/DrawerDivider'
import UnsupportedLine from 'components/feature/Simulation/UnsupportedLine'
import ContextualHelp from 'components/ContextualHelp'
import ContextualHelpFeatureOutput from 'components/ContextualHelp/ContextualHelpFeatureOutput'
import { getIsSchoolBusFeatureSubType } from 'selectors/feature/featureMetadata'
import {
  getHasNativeOutput,
  getHasNonBooleanNativeOutput,
} from 'selectors/feature/outputs'
import withStyle from './ConditionEditor.style'
import ConditionHelpAccordion from '../ConditionHelpAccordion'
import { ViewNotesButton } from '../../FeatureNotes/FeatureNotes'

/** eslint-disable react/jsx-props-no-spreading */

const SINGLE_CONDITION_LABELS = {
  [DIGITAL_INPUT]: strings.digital_input_pin,
  [ANALOG_INPUT]: strings.analog_input_pin,
  [NATIVE_INPUT]: strings.native_input,
  [CAN_INPUT]: strings.can_input,
  [SWITCH_INPUT]: strings.switch_input,
}
export const rungDisplayNames = (parentType, subType, index) => {
  if (parentType === COMPLEX) return strings.condition_label(inc(index))
  if (parentType === TIMER)
    return index === 0
      ? strings.activate_query_label
      : strings.reset_query_label
  if (subType) {
    return SINGLE_CONDITION_LABELS[subType]
  }
  return ''
}

const ConditionEditor = (props) => {
  const {
    className,
    rungType,
    parentType,
    parentQueryUri = '',
    inputUri,
    markedForDeletion,
    toggleMarkForDeletion,
    deleteCondition,
    resetCondition,
    isSingleCondition,
    conditionIndex,
    subType: originalSubtype,
    relatedOutput,
    fnId,
    showHelp,
    resetHelpPopup,
    addOutputNodeHandler,
    moduleName,
    selectedId,
    type,
    customDescription,
    ...rest
  } = props

  const isLogicSet = parentType === COMPLEX
  const isTimer = parentType === TIMER
  const currentSignal = useMakeSelector(makeSignalSelector, {
    signal: inputUri,
  })
  const isSwitchSignal = propOr([], 'switches', currentSignal).length > 0
  const subType = isSwitchSignal ? SWITCH_INPUT : originalSubtype || rungType
  const isSchoolBusFeature = useSelector(getIsSchoolBusFeatureSubType)
  const hasNativeOutput = useSelector(getHasNativeOutput, { id: fnId })
  const isNonBoolNativeOutput = useSelector(getHasNonBooleanNativeOutput, {
    id: fnId,
  })

  const isEditing = useSelector(getIsEditing)
  const editorProps = {
    ...rest,
    isLogicSet,
    inputUri,
    isSingleCondition,
    subType,
    fnId,
    customDescription,
    isNonBoolNativeOutput,
    disableFalseValue:
      hasNativeOutput &&
      parentQueryUri === '' &&
      [
        NATIVE_INPUT,
        DIGITAL_INPUT,
        SWITCH_INPUT,
        ANALOG_INPUT,
        CAN_INPUT,
      ].includes(subType),
  }
  const CarryEdge = () => (
    <>
      <SubQueryCarryEditor {...editorProps} />
      {isLogicSet && <EdgeEditor {...editorProps} />}
    </>
  )

  const CarryEdgeWithDataFrom = () => (
    <>
      <CarryEdge />
      <InputtingDataFrom refQueryUri={inputUri} />
    </>
  )

  const editorStrategies = {
    [COMPLEX]: <CarryEdgeWithDataFrom />,
    [CARRY_LOGIC_SET]: <CarryEdgeWithDataFrom />,
    [SELF_REFERENCE]: <CarryEdge />,
    [TIMER]: <TimerEditor {...editorProps} />,
    [CARRY_TIMER]: <TimerEditor {...editorProps} />,
    [ANALOG_INPUT]: <AnalogInputEditor {...editorProps} />,
    [DIGITAL_INPUT]: <DigitalInputEditor {...editorProps} />,
    [CAN_INPUT]: <CanInputEditor {...editorProps} />,
    [SIMPLE]: <SimpleEditor {...editorProps} />,
    [NATIVE_INPUT]: <SimpleEditor {...editorProps} />,
    [SWITCH_INPUT]: <SimpleEditor {...editorProps} />,
  }

  const handleCancelHelpPopup = () => {
    resetHelpPopup()
  }

  const handleAddOutputNode = () => {
    const tmpId = makeTempId()
    addOutputNodeHandler(tmpId, relatedOutput)
  }

  const displayName = rungDisplayNames(parentType, subType, conditionIndex)

  return (
    <Editor
      className={classNames(className, 'single-query-analog-input-editor')}
      toggleMarkForDeletion={toggleMarkForDeletion}
      markedForDeletion={markedForDeletion}
      deleteItem={deleteCondition}
      deleteHeaderText={strings.delete_item_heading(displayName)}
      deleteExplanation={strings.delete_condition_explanation}
      clearItem={resetCondition}
      clearHeaderText={strings.clear_item_heading(displayName)}
      clearExplanation={strings.clear_condition_explanation}
      isCondition={true}
      showDelete={isSingleCondition}
    >
      {!(isLogicSet || isTimer) && (
        <ViewNotesButton
          id={selectedId}
          fromType={type}
          description={displayName}
        />
      )}
      <Header type="h3" className="form-title">
        {displayName}
      </Header>
      <UnsupportedLine parentType={subType} />
      {isEditing && (
        <>
          <InputSelectionEditor {...props} subType={subType} />
          {editorStrategies[rungType]}
        </>
      )}

      {!isEditing && (
        <>
          <ReadOnly {...editorProps} />
          <ConditionHelpAccordion parentType={parentType} key={selectedId} />
          <DrawerDivider />
        </>
      )}

      {isSingleCondition && <OutputtingDataTo uri={rest.uri} />}
      {relatedOutput &&
        fnId &&
        showHelp &&
        parentType !== TIMER &&
        (moduleName === 'XMC3' || moduleName === 'XMC4') &&
        !isSchoolBusFeature && (
          <ContextualHelp
            contextMsg={strings.contextual_help_change_feature_output}
            contentMsg={strings.contextual_help_changes_to_this_output}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={20}
                height={20}
                fill="none"
              >
                <circle
                  cx={10}
                  cy={10}
                  r={9.5}
                  fill="#6C18BA"
                  stroke="#6C18BA"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M6.666 13.333 12.083 10 6.666 6.667v2.5H3.333v1.666h3.333v2.5z"
                  clipRule="evenodd"
                />
                <circle cx={15} cy={10} r={1.667} fill="#fff" />
              </svg>
            }
          >
            <ContextualHelpFeatureOutput
              relatedOutput={relatedOutput}
              onCancelClick={handleCancelHelpPopup}
              onAddClick={handleAddOutputNode}
            />
          </ContextualHelp>
        )}
    </Editor>
  )
}

/** eslint-enable react/jsx-props-no-spreading */

ConditionEditor.propTypes = {
  className: PropTypes.string,
  uri: PropTypes.string,
  isEditing: PropTypes.bool,
  rungType: PropTypes.string,
  affinityGroupNumber: PropTypes.number,
  highlightAffinity: PropTypes.bool,
  rungName: PropTypes.string,
  parentBoxPosition: PropTypes.number,
  childBoxPosition: PropTypes.number,
  parentType: PropTypes.string,
  inputUri: PropTypes.string,
  markedForDeletion: PropTypes.bool,
  toggleMarkForDeletion: PropTypes.func,
  deleteCondition: PropTypes.func,
  resetCondition: PropTypes.func,
  isSingleCondition: PropTypes.bool,
  conditionIndex: PropTypes.number,
  subType: PropTypes.string,
  relatedOutput: PropTypes.string,
  fnId: PropTypes.string,
  showHelp: PropTypes.bool,
  resetHelpPopup: PropTypes.func,
  addOutputNodeHandler: PropTypes.func,
  moduleName: PropTypes.string,
  selectedId: PropTypes.string,
  type: PropTypes.string,
  parentQueryUri: PropTypes.string,
  customDescription: PropTypes.string,
}

export default withStyle(ConditionEditor)
